import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    query,
    where
} from "firebase/firestore";
import { db } from "../firebase";
import { addDays, format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import { LinearProgress } from "@mui/material";

/* ===== CONSTANTS ===== */
const COL_WIDTH = 120;
const LEFT_COL_WIDTH = 260;
const PAGE_SIZE = 7;

function RoomAvailability() {
    const auth = getAuth();

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [roomTypes, setRoomTypes] = useState([]);
    const [roomCounts, setRoomCounts] = useState({});
    const [availability, setAvailability] = useState({});
    const [savingRoomId, setSavingRoomId] = useState(null);

    const [dates, setDates] = useState([]);
    const [page, setPage] = useState(0);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            setUserId(user.uid);

            const snap = await getDocs(collection(db, "authroles"));
            snap.forEach(d => {
                if (d.id === user.uid) {
                    setPropertyId(d.data().propertyId);
                }
            });
        });
        return () => unsub();
    }, []);

    /* ================= LOAD DATA ================= */
    useEffect(() => {
        if (!userId || !propertyId) return;
        loadAll();
    }, [userId, propertyId]);

    const loadAll = async () => {
        /* ROOM TYPES */
        const roomSnap = await getDocs(
            query(
                collection(db, "rooms"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );
        setRoomTypes(roomSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        /* ROOM COUNT FROM roominfos */
        const infoSnap = await getDocs(
            query(
                collection(db, "roominfos"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );

        const countMap = {};
        infoSnap.docs.forEach(d => {
            const r = d.data();
            countMap[r.roomTypeId] = (countMap[r.roomTypeId] || 0) + 1;
        });
        setRoomCounts(countMap);

        /* AVAILABILITY */
        const availSnap = await getDocs(
            query(
                collection(db, "roomtype_availability_calendar"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );

        const availMap = {};
        availSnap.docs.forEach(d => {
            const a = d.data();
            availMap[`${a.roomTypeId}_${a.date}`] = a.quantity;
        });
        setAvailability(availMap);
    };

    /* ================= DATES ================= */
    useEffect(() => {
        const arr = [];
        for (let i = 0; i < 180; i++) {
            arr.push(addDays(new Date(), i));
        }
        setDates(arr);
    }, []);

    const visibleDates = dates.slice(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE
    );

    /* ================= HANDLERS ================= */
    const changeQty = (roomTypeId, date, val) => {
        const maxRooms = roomCounts[roomTypeId] || 0;
        const qty = Number(val);

        if (qty > maxRooms) {
            toast.error(`Only ${maxRooms} rooms available`);
            return;
        }

        setAvailability(p => ({
            ...p,
            [`${roomTypeId}_${date}`]: qty
        }));
    };

    const bulkQty = async (roomTypeId) => {
        const maxRooms = roomCounts[roomTypeId] || 0;

        const { value } = await Swal.fire({
            title: "Bulk Availability",
            input: "number",
            inputLabel: `Max allowed: ${maxRooms}`,
            showCancelButton: true
        });

        if (value === undefined) return;

        if (Number(value) > maxRooms) {
            toast.error(`Cannot exceed ${maxRooms} rooms`);
            return;
        }

        const updated = { ...availability };
        dates.forEach(d => {
            updated[`${roomTypeId}_${format(d, "yyyy-MM-dd")}`] = Number(value);
        });

        setAvailability(updated);
        // toast.success("Bulk availability applied");
    };

    /* ================= SAVE ================= */
    const saveRoomType = async (roomTypeId) => {
        try {
            const maxRooms = roomCounts[roomTypeId] || 0;
            setSavingRoomId(roomTypeId); // 🔥 START LOADER
            for (let d of dates) {
                const date = format(d, "yyyy-MM-dd");
                const key = `${roomTypeId}_${date}`;
                const qty = Number(availability[key]);

                if (qty > maxRooms) {
                    toast.error(`Qty exceeds total rooms (${maxRooms})`);
                    return;
                }

                if (availability[key] === undefined) continue;

                await setDoc(
                    doc(db, "roomtype_availability_calendar", key),
                    {
                        roomTypeId,
                        date,
                        quantity: qty,
                        totalRooms: maxRooms,
                        userId,
                        propertyId,
                        updatedAt: new Date()
                    }
                );
            }

            toast.success("Availability saved");
        } catch {
            toast.error("Save failed");
        }
        finally {
            setSavingRoomId(null); // ✅ STOP LOADER
        }
    };

    const isWeekend = (date) => {
        const day = format(date, "EEE");
        return day === "Sat" || day === "Sun";
    };

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Menu />

            <Box className="content-wrapper" flex={1} p={3}>
                <ToastContainer autoClose={2000} />

                <Typography variant="h5" fontWeight={800} mb={2}>
                    Room Availability
                </Typography>

                <Card sx={{ borderRadius: 3 }}>
                    <CardContent>

                        {/* NAV */}
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <IconButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                                <ArrowBack />
                            </IconButton>
                            <IconButton
                                disabled={(page + 1) * PAGE_SIZE >= dates.length}
                                onClick={() => setPage(p => p + 1)}
                            >
                                <ArrowForward />
                            </IconButton>
                        </Box>

                        {/* GRID */}
                        <Box sx={{ overflowX: "auto" }}>
                            {/* HEADER */}
                            <Box sx={{ display: "flex", position: "sticky", top: 0, bgcolor: "#fff" }}>
                                <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1, fontWeight: 700 }}>
                                    Room Type
                                </Box>
                                {visibleDates.map(d => (
                                    <Box
                                        key={d}
                                        sx={{
                                            width: COL_WIDTH,
                                            textAlign: "center",
                                            p: 1,
                                            bgcolor: isWeekend(d) ? "#fff5f5" : "transparent"
                                        }}
                                    >
                                        <Typography fontSize={12} fontWeight={600}>
                                            {format(d, "EEE")}
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={700}>
                                            {format(d, "dd MMM")}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* BODY */}
                            {roomTypes.map(rt => (
                                <Box key={rt.id}>
                                    <Box sx={{ display: "flex", bgcolor: "#fafafa" }}>
                                        <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1, fontWeight: 600 }}>
                                            {rt.roomTypeName}
                                            <Typography fontSize={12}>
                                                Total Rooms: {roomCounts[rt.id] || 0}
                                            </Typography>
                                            <Button size="small" onClick={() => bulkQty(rt.id)}>
                                                Bulk
                                            </Button>
                                        </Box>

                                        {visibleDates.map(d => {
                                            const date = format(d, "yyyy-MM-dd");
                                            return (
                                                <Box key={date} sx={{ width: COL_WIDTH, p: 0.5 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        fullWidth
                                                        inputProps={{
                                                            min: 0,
                                                            max: roomCounts[rt.id] || 0
                                                        }}
                                                        value={availability[`${rt.id}_${date}`] || ""}
                                                        onChange={e =>
                                                            changeQty(rt.id, date, e.target.value)
                                                        }
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    <Box textAlign="right" my={1}>
                                        <Button
                                            variant="contained"
                                            disabled={savingRoomId === rt.id}
                                            onClick={() => saveRoomType(rt.id)}
                                            sx={{ minWidth: 180 }}
                                        >
                                            {savingRoomId === rt.id ? "Saving..." : `Save ${rt.roomTypeName}`}
                                        </Button>



                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default RoomAvailability;
